import { useMemo } from "react";
import { Hero as BaseHero } from "components/src/components/Hero/Hero";
import { Badge } from "components/src/components/Badge/Badge";
import { Icon } from "components/src/components/Icon/Icon";
import { SbkHeroProps } from "./types";
import { getCompetitionInfo } from "shared/src/hooks/getCompetitionInfo";
import { getSportInfo } from "shared/src/hooks/getSportInfo";
import competitionTypeMap from "tokens/build/js/competition-type-map";

/**
 * Sportsbook Hero component that extends the Core Hero with sports-specific features.
 * Automatically resolves background images, icons, and metadata from sport/competition data.
 * 
 * @example
 * ```tsx
 * <Hero
 *   sport="tennis"
 *   competition="Men's Australian Open"
 *   isLive
 *   subtext1="Grand Slam"
 *   subtext2="Today at 3:00 PM ET"
 *   showPrimaryCta
 * />
 * ```
 * 
 * @param sport - Sport name to auto-resolve background image and icon from sport-map
 * @param competition - Competition name to auto-resolve background, title, and location
 * @param competitionType - Competition type to derive sport and background from competition-type-map
 * @param subtext1 - Small text displayed above the title, useful for league name or category
 * @param subtext2 - Text displayed below the title, useful for description or event start time
 * @param isLive - Shows a "Live" badge before subtext1
 * @param showLocation - Shows competition location with country flag in subtext3 (default: true)
 * @param showLeading - Shows leading element, defaults to sport icon (default: true)
 * @param forceTitle - If true and title is a string, shows title instead of competition name (default: false)
 * @param imageUrl - Override URL for background image (takes precedence over auto-resolved)
 * 
 * Background resolution priority:
 * 1. imageUrl prop (if provided)
 * 2. Competition backgrounds (from competitions-map)
 * 3. CompetitionType backgrounds (from competition-type-map)
 * 4. Sport backgrounds (first image from sport-map)
 */
export const Hero = ({ sport, competition, competitionType, imageUrl, isLive, showLocation = true, showLeading = true, forceTitle = false, leading, title, subtext1, options, ref, ...rest }: SbkHeroProps) => {
    const compData = useMemo(() => competition ? getCompetitionInfo(competition) : null, [competition]);

    const resolvedTitle = useMemo(() => {
        if (forceTitle && typeof title === "string") return title;
        if (compData?.name) return compData.name;
        return title;
    }, [forceTitle, title, compData]);

    const resolvedImageUrl = useMemo(() => {
        if (imageUrl) return imageUrl;
        
        // Try competition first (backgrounds in metadata.images)
        if (compData?.metadata?.images?.backgrounds) {
            const backgrounds = compData.metadata.images.backgrounds;
            if (Array.isArray(backgrounds) && backgrounds.length > 0) {
                const bg = backgrounds[0];
                return `${bg.folder}/${bg.name}.${bg.ext}`;
            }
        }
        
        // Try competitionType
        if (competitionType && competitionTypeMap && competitionType in competitionTypeMap) {
            const typeData = competitionTypeMap[competitionType];
            const backgrounds = typeData?.backgrounds;
            if (Array.isArray(backgrounds) && backgrounds.length > 0) {
                const bg = backgrounds[0];
                return `${bg.folder}/${bg.name}.${bg.ext}`;
            }
        }
        
        // Fall back to sport (backgrounds at metadata.images.backgrounds) - use first
        if (sport) {
            const sportData = getSportInfo(sport);
            const backgrounds = sportData?.metadata?.images?.backgrounds;
            if (Array.isArray(backgrounds) && backgrounds.length > 0) {
                const bg = backgrounds[0];
                return `${bg.folder}/${bg.name}.${bg.ext}`;
            }
        }
        
        return undefined;
    }, [sport, compData, competitionType, imageUrl]);

    const resolvedSubtext1 = useMemo(() => {
        if (isLive) {
            return (
                <span className="cui-flex cui-items-center cui-gap-xs">
                    <Badge text="Live" shape="square" bgColor="highlight-accent-minimal" fgColor="highlight-accent" icon={{ name: "icon_ui_live_animated", size: 8 }} />
                    {subtext1}
                </span>
            );
        }
        return subtext1;
    }, [isLive, subtext1]);

    const resolvedSubtext3 = useMemo(() => {
        if (!showLocation || !compData?.location?.location) return undefined;
        const flagName = compData.metadata?.images?.location?.name;
        return (
            <span className="cui-flex cui-items-center cui-gap-xs">
                {flagName && <Icon name={flagName} />}
                {compData.location.location}
            </span>
        );
    }, [showLocation, compData]);

    const resolvedOptions = useMemo(() => ({
        subtext3TextColor: "fg-global-white",
        subtext3TextSize: "body-sm",
        ...options
    }), [options]);

    const resolvedLeading = useMemo(() => {
        if (!showLeading) return undefined;
        if (leading) return leading;
        // Prefer competition brand icon
        const compIconName = compData?.metadata?.images?.primary?.name;
        if (compIconName) return <Icon name={compIconName} />;
        // Fall back to sport icon
        const sportData = sport ? getSportInfo(sport) : null;
        const iconName = sportData?.metadata?.images?.icon?.name;
        return iconName ? <Icon name={iconName} /> : undefined;
    }, [showLeading, leading, sport, compData]);

    return <BaseHero imageUrl={resolvedImageUrl} leading={resolvedLeading} title={resolvedTitle} subtext1={resolvedSubtext1} subtext3={resolvedSubtext3} options={resolvedOptions} {...rest} />;
};
